import { useEffect, useState } from 'react';
import PageHeader from '@/components/PageHeader';
import { fetchAdminUsers, createAdminUser, updateAdminUser, deleteAdminUser, changePassword } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { UserPlus, KeyRound, Shield, User as UserIcon, Trash2, Edit3, Lock, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '@/lib/auth';

const emptyForm = { email: '', password: '', name: '', role: 'user' };

export default function AdminUsers() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    // create
    const [openCreate, setOpenCreate] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [busy, setBusy] = useState(false);
    // edit
    const [editing, setEditing] = useState(null); // user object
    // reset password
    const [resetting, setResetting] = useState(null);
    const [newPw, setNewPw] = useState('');
    // change own password
    const [openMyPw, setOpenMyPw] = useState(false);
    const [pwCurrent, setPwCurrent] = useState('');
    const [pwNew, setPwNew] = useState('');
    const [pwConfirm, setPwConfirm] = useState('');

    const load = async () => {
        setLoading(true);
        try { setUsers(await fetchAdminUsers()); }
        catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to load users'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const submitCreate = async () => {
        if (!form.email.trim() || !form.password) { toast.error('Email and password required'); return; }
        if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        setBusy(true);
        try {
            await createAdminUser({ ...form, email: form.email.trim() });
            toast.success(`Created ${form.email}`);
            setOpenCreate(false); setForm(emptyForm); load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Create failed'); }
        setBusy(false);
    };

    const saveEdit = async () => {
        setBusy(true);
        try {
            await updateAdminUser(editing.id, { name: editing.name, role: editing.role, active: editing.active });
            toast.success('User updated');
            setEditing(null); load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Update failed'); }
        setBusy(false);
    };

    const doReset = async () => {
        if (newPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        setBusy(true);
        try {
            await updateAdminUser(resetting.id, { password: newPw });
            toast.success(`Password reset for ${resetting.email}`);
            setResetting(null); setNewPw('');
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Reset failed'); }
        setBusy(false);
    };

    const remove = async (u) => {
        if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
        try {
            await deleteAdminUser(u.id);
            toast.success('User deleted');
            load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Delete failed'); }
    };

    const submitMyPw = async () => {
        if (pwNew !== pwConfirm) { toast.error('New passwords do not match'); return; }
        if (pwNew.length < 6) { toast.error('New password must be at least 6 characters'); return; }
        setBusy(true);
        try {
            await changePassword(pwCurrent, pwNew);
            toast.success('Password changed');
            setOpenMyPw(false); setPwCurrent(''); setPwNew(''); setPwConfirm('');
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Change failed'); }
        setBusy(false);
    };

    return (
        <div>
            <PageHeader
                title="Admin · User Management"
                subtitle={`${users.length} accounts`}
                actions={
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setOpenMyPw(true)} data-testid="btn-change-my-password" className="border-[#E2E8F0] bg-transparent text-slate-900 hover:bg-[#FFFFFF] gap-2">
                            <KeyRound className="w-4 h-4" /> My Password
                        </Button>
                        <Button onClick={() => setOpenCreate(true)} data-testid="btn-add-user" className="bg-[#016a6d] hover:bg-[#014e50] gap-2">
                            <UserPlus className="w-4 h-4" /> Add User
                        </Button>
                    </div>
                }
            />

            <div className="p-6">
                <div className="surface-1 rounded-md overflow-hidden" data-testid="users-table">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#F1F5F9] border-b border-[#E2E8F0]">
                                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    <th className="px-4 py-3">User</th>
                                    <th className="px-4 py-3">Role</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Created</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr><td colSpan={5} className="text-center py-10 text-slate-500">Loading users…</td></tr>
                                )}
                                {!loading && users.map(u => (
                                    <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-[#E2E8F0] hover:bg-[#FFFFFF]">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${u.role === 'admin' ? 'bg-[#016a6d]/15 text-[#016a6d]' : 'bg-[#FFFFFF] text-slate-500 border border-[#E2E8F0]'}`}>
                                                    {u.role === 'admin' ? <Shield className="w-4 h-4" /> : <UserIcon className="w-4 h-4" />}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="text-slate-900 font-medium truncate">{u.name || '—'}{u.id === me?.id && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#016a6d]">You</span>}</div>
                                                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${u.role === 'admin' ? 'bg-[#016a6d]/10 text-[#016a6d] border-[#016a6d]/30' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                                                {u.role}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            {u.active !== false ? (
                                                <span className="text-xs text-[#34C759] flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Active</span>
                                            ) : (
                                                <span className="text-xs text-slate-500 flex items-center gap-1"><XCircle className="w-3 h-3" /> Disabled</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-slate-500">{formatDate(u.created_at)}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-1">
                                                <button
                                                    onClick={() => setEditing({ ...u })}
                                                    data-testid={`btn-edit-${u.email}`}
                                                    className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-[#F1F5F9] rounded transition-colors"
                                                    title="Edit"
                                                >
                                                    <Edit3 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => { setResetting(u); setNewPw(''); }}
                                                    data-testid={`btn-reset-${u.email}`}
                                                    className="p-1.5 text-slate-500 hover:text-[#FF9F0A] hover:bg-[#F1F5F9] rounded transition-colors"
                                                    title="Reset password"
                                                >
                                                    <Lock className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => remove(u)}
                                                    disabled={u.id === me?.id}
                                                    data-testid={`btn-delete-${u.email}`}
                                                    className="p-1.5 text-slate-500 hover:text-[#FF453A] hover:bg-[#F1F5F9] rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                    title={u.id === me?.id ? "You can't delete yourself" : "Delete"}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && users.length === 0 && (
                                    <tr><td colSpan={5} className="text-center py-10 text-slate-500">No users yet</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="mt-4 text-xs text-slate-500 space-y-1">
                    <div>• <span className="text-[#016a6d]">Admins</span> can manage all users and access every module.</div>
                    <div>• <span className="text-slate-500">Users</span> can access the full CRM (leads, pipeline, analytics) but not this admin panel.</div>
                    <div>• Disabled users can't sign in. Delete permanently removes the account.</div>
                </div>
            </div>

            {/* Create dialog */}
            <Dialog open={openCreate} onOpenChange={setOpenCreate}>
                <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl">Add User</DialogTitle>
                        <DialogDescription className="text-slate-500">The user will be able to sign in immediately with the credentials below.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="overline block mb-1">Email *</label>
                            <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-9" placeholder="user@ikascorp.com" />
                        </div>
                        <div>
                            <label className="overline block mb-1">Full Name</label>
                            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-9" placeholder="Optional" />
                        </div>
                        <div>
                            <label className="overline block mb-1">Password * <span className="text-slate-400 normal-case tracking-normal">(≥ 6 chars)</span></label>
                            <Input type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-9 font-mono" />
                        </div>
                        <div>
                            <label className="overline block mb-1">Role</label>
                            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} data-testid="new-user-role" className="w-full h-9 bg-[#F1F5F9] border border-[#E2E8F0] rounded-md text-slate-900 text-sm px-3">
                                <option value="user">User (Sales Team Member)</option>
                                <option value="admin">Admin (Full access + manage users)</option>
                            </select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="border-[#E2E8F0] bg-transparent text-slate-900" onClick={() => setOpenCreate(false)}>Cancel</Button>
                        <Button onClick={submitCreate} disabled={busy} data-testid="btn-save-user" className="bg-[#016a6d] hover:bg-[#014e50]">
                            {busy ? 'Creating…' : 'Create User'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-md">
                    {editing && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display text-xl">Edit {editing.email}</DialogTitle>
                                <DialogDescription className="text-slate-500">Update user profile, role, or activation status.</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3">
                                <div>
                                    <label className="overline block mb-1">Full Name</label>
                                    <Input value={editing.name || ''} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="edit-user-name" className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-9" />
                                </div>
                                <div>
                                    <label className="overline block mb-1">Role</label>
                                    <select value={editing.role} onChange={e => setEditing({ ...editing, role: e.target.value })} data-testid="edit-user-role" className="w-full h-9 bg-[#F1F5F9] border border-[#E2E8F0] rounded-md text-slate-900 text-sm px-3">
                                        <option value="user">User</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </div>
                                <label className="flex items-center gap-2 text-sm text-slate-700">
                                    <input type="checkbox" checked={editing.active !== false} onChange={e => setEditing({ ...editing, active: e.target.checked })} data-testid="edit-user-active" className="accent-[#016a6d]" />
                                    Account active
                                </label>
                                {editing.id === me?.id && (
                                    <div className="text-xs text-[#FF9F0A]">You are editing your own account — you cannot disable yourself or demote the last admin.</div>
                                )}
                            </div>
                            <DialogFooter>
                                <Button variant="outline" className="border-[#E2E8F0] bg-transparent text-slate-900" onClick={() => setEditing(null)}>Cancel</Button>
                                <Button onClick={saveEdit} disabled={busy} data-testid="btn-save-edit" className="bg-[#016a6d] hover:bg-[#014e50]">Save</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Reset password dialog */}
            <Dialog open={!!resetting} onOpenChange={(o) => !o && setResetting(null)}>
                <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-md">
                    {resetting && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display text-xl">Reset password for {resetting.email}</DialogTitle>
                                <DialogDescription className="text-slate-500">Share this new password with the user securely. They can change it after signing in.</DialogDescription>
                            </DialogHeader>
                            <div>
                                <label className="overline block mb-1">New Password (≥ 6 chars)</label>
                                <Input type="text" value={newPw} onChange={e => setNewPw(e.target.value)} data-testid="reset-password-input" className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-9 font-mono" />
                            </div>
                            <DialogFooter>
                                <Button variant="outline" className="border-[#E2E8F0] bg-transparent text-slate-900" onClick={() => setResetting(null)}>Cancel</Button>
                                <Button onClick={doReset} disabled={busy} data-testid="btn-confirm-reset" className="bg-[#FF9F0A] hover:bg-[#d98a0a] text-black">Reset Password</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* My password dialog */}
            <Dialog open={openMyPw} onOpenChange={setOpenMyPw}>
                <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl">Change My Password</DialogTitle>
                        <DialogDescription className="text-slate-500">Update the password for your own account.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="overline block mb-1">Current Password</label>
                            <Input type="password" value={pwCurrent} onChange={e => setPwCurrent(e.target.value)} data-testid="pw-current" className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-9" />
                        </div>
                        <div>
                            <label className="overline block mb-1">New Password (≥ 6 chars)</label>
                            <Input type="password" value={pwNew} onChange={e => setPwNew(e.target.value)} data-testid="pw-new" className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-9" />
                        </div>
                        <div>
                            <label className="overline block mb-1">Confirm New Password</label>
                            <Input type="password" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} data-testid="pw-confirm" className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-9" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="border-[#E2E8F0] bg-transparent text-slate-900" onClick={() => setOpenMyPw(false)}>Cancel</Button>
                        <Button onClick={submitMyPw} disabled={busy} data-testid="btn-submit-my-pw" className="bg-[#016a6d] hover:bg-[#014e50]">Change Password</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
